import React, { useCallback } from 'react';
import { useIntl, defineMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport } from '@fortawesome/pro-light-svg-icons/faFileExport';
import { useTRPCClientAPI, useTRPCMutation } from '../../../../trpc/trpc';
import { saveCsvFile } from '../../utils/exportCsv';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { LockedControl } from '../LockedControl/LockedControl';
import styles from './ExportCsvButton.module.scss';

export type WatchlistExportKind = 'tracks' | 'artists' | 'playlists';

interface ExportCsvButtonProps {
  /** The Watchlist list to export. The server exports every follow of it, not the page on screen. */
  kind: WatchlistExportKind;
  filename: string;
  /** True when the list has no follow: nothing to export. */
  isEmpty: boolean;
}

const messages = {
  export: defineMessage({
    id: 'app.sml.exportCsv.label',
    defaultMessage: 'Export CSV',
  }),
};

/**
 * Saves a whole Watchlist list as a CSV file. The server builds the file (`watchlist.exportCsv`)
 * and checks the plan (`canExport`: Pro and up, and the trial). On another plan the button shows
 * the lock and opens the upgrade prompt, like every other plan-limited control (design free-plans
 * n). A refusal from the server opens the same prompt (the mutation cache reads `M24_LIMIT_*`).
 */
export function ExportCsvButton({ kind, filename, isEmpty }: ExportCsvButtonProps): React.ReactElement {
  const intl = useIntl();
  const { canExport } = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  const trpcClient = useTRPCClientAPI();

  const exportMutation = useTRPCMutation({
    mutationFn: async () => trpcClient.watchlist.exportCsv.query({ kind }),
    onSuccess: (result) => saveCsvFile(filename, result.csv),
  });
  const { mutate, isPending } = exportMutation;

  const handleClick = useCallback(() => mutate(), [mutate]);

  const openPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.csvExport), [openUpgradePrompt]);

  if (!canExport) {
    return (
      <LockedControl
        variant="button"
        size="sm"
        feature={SMLUpgradeFeature.csvExport}
        label={intl.formatMessage(messages.export)}
        omitCap
        onClick={openPrompt}
      />
    );
  }

  return (
    <button type="button" className={styles.button} onClick={handleClick} disabled={isEmpty || isPending}>
      <FontAwesomeIcon icon={faFileExport} className={styles.icon} />
      {intl.formatMessage(messages.export)}
    </button>
  );
}
